import React from 'react';
import { CrowdVoltListing } from '../types';
import { Button, Flex, Text, Card } from './base-components';
import { useListings, useLinkCrowdVoltListing } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { trackEvent } from '../services/analytics';

interface Props {
  cvListing: CrowdVoltListing | null;
  open: boolean;
  onClose: () => void;
}

export const LinkListingModal: React.FC<Props> = ({ cvListing, open, onClose }) => {
  const { showSuccess, showError } = useToast();
  // Filter by event if ft_event_id is available, else show all active
  const { data, isLoading } = useListings({
    status: 'ACTIVE',
    ...(cvListing?.ft_event_id ? { event_id: cvListing.ft_event_id } : {}),
  });
  const linkMutation = useLinkCrowdVoltListing();

  if (!open || !cvListing) return null;

  const activeFTListings = data?.data ?? [];

  const handleLink = async (ftListingId: number) => {
    try {
      await linkMutation.mutateAsync({
        ask_uqid: cvListing.ask_uqid,
        ft_listing_id: ftListingId,
        event_uqid: cvListing.event_uqid,
      });
      showSuccess('Listing linked — price mirroring enabled');
      trackEvent('cv_listing_linked', { askUqid: cvListing.ask_uqid, ftListingId: String(ftListingId) });
      onClose();
    } catch (err: any) {
      showError(err?.message || 'Failed to link listing');
    }
  };

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      animate="fadeIn"
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="md"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={1}>Link to FT Listing</Text>
        <Text variant="muted" mb={4}>
          Choose an active FT listing to mirror prices for:
          <span className="text-white/80"> {cvListing.event_name}</span>
        </Text>

        <div className="max-h-80 overflow-y-auto space-y-2">
          {isLoading && (
            <div className="py-8 text-center">
              <Text variant="muted">Loading listings…</Text>
            </div>
          )}

          {!isLoading && activeFTListings.length === 0 && (
            <div className="py-8 text-center">
              <Text variant="muted">No active FT listings found for this event</Text>
              <Text variant="muted" size="xs" mt={1}>
                Create a listing first to link it here
              </Text>
            </div>
          )}

          {activeFTListings.map((listing: any) => (
            <button
              key={listing.id}
              onClick={() => handleLink(listing.id)}
              disabled={linkMutation.isPending}
              className="w-full flex items-center justify-between p-3 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.06] hover:border-[#3b82f6]/30 transition-all text-left disabled:opacity-50"
            >
              <div>
                <p className="text-sm font-medium text-white">
                  {listing.event?.name || 'Unknown Event'}
                </p>
                <p className="text-xs text-white/40 mt-0.5">
                  {listing.quantity} ticket{listing.quantity !== 1 ? 's' : ''} · #{listing.id}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-white">
                  ${parseFloat(listing.price_per_ticket).toFixed(2)}
                </p>
                <p className="text-xs text-white/40">per ticket</p>
              </div>
            </button>
          ))}
        </div>

        <Flex justify="end" mt={4}>
          <Button variant="secondary" onClick={onClose} disabled={linkMutation.isPending}>
            Cancel
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
};
